import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { useSnackbar } from 'notistack';
import { changePublicationConfigValues } from '../../api/changePublicationConfigValues';
import {
  buildSinglePayOptionValue,
  fetchPaymentCards,
  fetchPublicationPaymentMethods,
  getPaymentCardId,
  mapPaymentCardsToOptions,
  OnePaymentPromotion,
  OnePaymentPublication,
  PaymentCard,
  publicationHasSinglePayOption,
  PublicationPaymentMethodsResult,
} from '../../api/paymentCards';
import { FORM_COLORS } from '../PublicationFormModal/constants';
import { ZoneMultiSelect } from '../PublicationsHeader/components/ZoneMultiSelect';

interface TableColumn<T> {
  id: keyof T | string;
  label: string;
  render?: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  title: string;
  rows: T[];
  columns: TableColumn<T>[];
  loading: boolean;
  emptyMessage: string;
  rowKey: (row: T, index: number) => string;
  wrapClassName?: string;
  tableClassName?: string;
  getCellClassName?: (columnId: string) => string | undefined;
}

const PROMOTION_COLUMNS: TableColumn<OnePaymentPromotion>[] = [
  { id: 'promotion_id', label: 'ID promoción' },
  { id: 'name', label: 'Promoción' },
];

const PUBLICATION_TABLE_COLUMN_COUNT = 4;

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 14,
    width: 1040,
    maxWidth: '96vw',
  },
  body: {
    padding: '26px 30px 30px',
    fontFamily: 'inherit',
    color: FORM_COLORS.text,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  title: {
    fontWeight: 600,
    fontSize: 16,
    color: FORM_COLORS.text,
  },
  subtitle: {
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
  },
  panel: {
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 10,
    background: FORM_COLORS.white,
    padding: 14,
    marginBottom: 18,
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    margin: 0,
  },
  selectedCount: {
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
  },
  selectWrap: {
    maxWidth: 420,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '36px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px 0',
    color: FORM_COLORS.textSecondary,
    fontSize: 13,
  },
  errorState: {
    border: `0.5px solid ${FORM_COLORS.redBorder}`,
    borderRadius: 8,
    background: FORM_COLORS.redBg,
    color: FORM_COLORS.red,
    fontSize: 13,
    padding: '10px 12px',
    marginBottom: 14,
  },
  tablesGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(180px, 220px)',
    gap: 16,
    alignItems: 'start',
  },
  publicationsTableWrap: {
    minWidth: 0,
  },
  promotionsTableWrap: {
    minWidth: 0,
  },
  tableContainer: {
    maxHeight: 310,
    border: `1px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    overflow: 'auto',
  },
  publicationsTable: {
    tableLayout: 'fixed',
    width: '100%',
  },
  publicationIdCell: {
    width: 120,
    fontFamily: 'monospace',
    fontSize: 11,
  },
  publicationNameCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.35,
  },
  publicationSkuCell: {
    width: 120,
  },
  promotionsTable: {
    tableLayout: 'fixed',
    width: '100%',
  },
  promotionIdCell: {
    width: 72,
  },
  promotionNameCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.35,
  },
  tableHead: {
    background: '#f7f7f9',
    '& th': {
      fontWeight: 600,
      fontSize: 12,
      color: FORM_COLORS.textSecondary,
      padding: '8px 10px',
      borderBottom: `1px solid ${FORM_COLORS.border}`,
      whiteSpace: 'nowrap',
    },
  },
  tableRow: {
    '&:last-child td': {
      borderBottom: 'none',
    },
    '& td': {
      fontSize: 12,
      padding: '7px 10px',
      borderBottom: `1px solid ${FORM_COLORS.border}`,
      verticalAlign: 'top',
    },
  },
  checkboxCell: {
    width: 42,
    maxWidth: 42,
    padding: '4px 8px !important',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
    paddingTop: 18,
    borderTop: `0.5px solid ${FORM_COLORS.border}`,
  },
  ghostButton: {
    background: 'none',
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    cursor: 'pointer',
    color: FORM_COLORS.textSecondary,
    fontFamily: 'inherit',
  },
  primaryButton: {
    background: FORM_COLORS.pink,
    color: '#fff',
    border: `1.5px solid ${FORM_COLORS.pink}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },
}));

const DataTable = <T,>({
  title,
  rows,
  columns,
  loading,
  emptyMessage,
  rowKey,
  wrapClassName,
  tableClassName,
  getCellClassName,
}: DataTableProps<T>) => {
  const classes = useStyles();

  return (
    <div className={wrapClassName}>
      <div className={classes.panelHeader}>
        <p className={classes.sectionTitle}>{title}</p>
        <span className={classes.selectedCount}>
          {rows.length}
          {' registros'}
        </span>
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader className={tableClassName}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  className={getCellClassName?.(String(column.id))}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <div className={classes.loadingWrapper}>
                    <CircularProgress size={28} />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography className={classes.emptyState}>{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={rowKey(row, index)} className={classes.tableRow}>
                  {columns.map((column) => (
                    <TableCell
                      key={String(column.id)}
                      className={getCellClassName?.(String(column.id))}
                    >
                      {column.render
                        ? column.render(row)
                        : String((row as Record<string, unknown>)[column.id as string] ?? '-')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

interface PublicationsTableProps {
  rows: OnePaymentPublication[];
  loading: boolean;
  emptyMessage: string;
  selectedPublicationIds: Set<string>;
  onTogglePublication: (publicationId: string) => void;
  onToggleAllPublications: () => void;
}

const PublicationsTable = ({
  rows,
  loading,
  emptyMessage,
  selectedPublicationIds,
  onTogglePublication,
  onToggleAllPublications,
}: PublicationsTableProps) => {
  const classes = useStyles();
  const allSelected = rows.length > 0 && rows.every((row) => selectedPublicationIds.has(String(row.publication_id)));
  const someSelected = rows.some((row) => selectedPublicationIds.has(String(row.publication_id)));

  return (
    <div className={classes.publicationsTableWrap}>
      <div className={classes.panelHeader}>
        <p className={classes.sectionTitle}>Publicaciones</p>
        <span className={classes.selectedCount}>
          {selectedPublicationIds.size}
          {' seleccionadas · '}
          {rows.length}
          {' registros'}
        </span>
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader className={classes.publicationsTable}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.checkboxCell} padding="checkbox">
                <Checkbox
                  color="primary"
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  disabled={loading || rows.length === 0}
                  onChange={onToggleAllPublications}
                />
              </TableCell>
              <TableCell className={classes.publicationIdCell}>Pub. ID</TableCell>
              <TableCell>Producto</TableCell>
              <TableCell className={classes.publicationSkuCell}>SKU</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={PUBLICATION_TABLE_COLUMN_COUNT}>
                  <div className={classes.loadingWrapper}>
                    <CircularProgress size={28} />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={PUBLICATION_TABLE_COLUMN_COUNT}>
                  <Typography className={classes.emptyState}>{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const publicationId = String(row.publication_id);
                const selected = selectedPublicationIds.has(publicationId);

                return (
                  <TableRow key={publicationId} className={classes.tableRow} selected={selected}>
                    <TableCell className={classes.checkboxCell} padding="checkbox">
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={selected}
                        onChange={() => onTogglePublication(publicationId)}
                      />
                    </TableCell>
                    <TableCell className={classes.publicationIdCell}>{row.publication_id}</TableCell>
                    <TableCell className={classes.publicationNameCell}>{row.name}</TableCell>
                    <TableCell className={classes.publicationSkuCell}>{row.sku || '-'}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

interface OnePaymentModalProps {
  open: boolean;
  onClose: () => void;
  onApplied?: () => void;
}

export const OnePaymentModal = ({ open, onClose, onApplied }: OnePaymentModalProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [selectedCardIds, setSelectedCardIds] = useState<number[]>([]);
  const [selectedPublicationIds, setSelectedPublicationIds] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<PublicationPaymentMethodsResult>({
    publications: [],
    paymentMethods: [],
  });
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardOptions = useMemo(() => mapPaymentCardsToOptions(cards), [cards]);
  const selectedCardIdsAsString = useMemo(
    () => selectedCardIds.map(String),
    [selectedCardIds],
  );

  const loadCards = useCallback(async () => {
    setLoadingCards(true);
    setError(null);

    try {
      const nextCards = await fetchPaymentCards();
      setCards(nextCards);
    } catch {
      setCards([]);
      setError('No se pudieron cargar las tarjetas de 1 pago.');
    } finally {
      setLoadingCards(false);
    }
  }, []);

  const loadResults = useCallback(async (cardIds: number[]) => {
    if (!cardIds.length) {
      setResult({ publications: [], paymentMethods: [] });
      return;
    }

    setLoadingResults(true);
    setError(null);

    try {
      const nextResult = await fetchPublicationPaymentMethods(cardIds);
      setResult(nextResult);
    } catch {
      setResult({ publications: [], paymentMethods: [] });
      setError('No se pudieron cargar las publicaciones y formas de pago asociadas.');
    } finally {
      setLoadingResults(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setCards([]);
      setSelectedCardIds([]);
      setSelectedPublicationIds(new Set());
      setResult({ publications: [], paymentMethods: [] });
      setError(null);
      return;
    }

    loadCards();
  }, [open, loadCards]);

  useEffect(() => {
    if (!open) return;
    loadResults(selectedCardIds);
  }, [open, selectedCardIds, loadResults]);

  useEffect(() => {
    if (!open || loadingResults) return;

    setSelectedPublicationIds(
      new Set(
        result.publications
          .filter(publicationHasSinglePayOption)
          .map((publication) => String(publication.publication_id)),
      ),
    );
  }, [open, loadingResults, result.publications]);

  const selectedSinglePayOption = useMemo(
    () =>
      selectedCardIds
        .map((cardId) => {
          const card = cards.find((item) => getPaymentCardId(item) === cardId);
          return card ? buildSinglePayOptionValue(card) : null;
        })
        .filter((value): value is string => value != null)
        .join(', '),
    [cards, selectedCardIds],
  );

  const handleCardSelectionChange = (nextSelectedIds: string[]) => {
    setSelectedCardIds(nextSelectedIds.map(Number));
  };

  const handleTogglePublication = (publicationId: string) => {
    setSelectedPublicationIds((current) => {
      const next = new Set(current);
      if (next.has(publicationId)) {
        next.delete(publicationId);
      } else {
        next.add(publicationId);
      }
      return next;
    });
  };

  const handleToggleAllPublications = () => {
    setSelectedPublicationIds((current) => {
      const allIds = result.publications.map((publication) => String(publication.publication_id));
      const allSelected = allIds.length > 0 && allIds.every((id) => current.has(id));

      if (allSelected) return new Set();

      return new Set(allIds);
    });
  };

  const handleApply = async () => {
    if (!selectedCardIds.length) {
      enqueueSnackbar('Debes seleccionar al menos una tarjeta', { variant: 'error' });
      return;
    }

    if (!selectedSinglePayOption) {
      enqueueSnackbar('Las tarjetas seleccionadas no tienen promoción asociada', { variant: 'error' });
      return;
    }

    const allPublications = result.publications;
    const selectedPublications = allPublications.filter((publication) =>
      selectedPublicationIds.has(String(publication.publication_id)),
    );
    const selectedIds = new Set(selectedPublications.map((publication) => String(publication.publication_id)));
    const deselectedPublications = allPublications.filter(
      (publication) => !selectedIds.has(String(publication.publication_id)),
    );

    setApplying(true);
    setError(null);

    try {
      await Promise.all([
        ...selectedPublications.map((publication) => {
          const currentConfigValues = publication.config_values ?? {};

          return changePublicationConfigValues({
            publicationId: publication.publication_id,
            configValues: {
              ...currentConfigValues,
              singlePayOption: selectedSinglePayOption,
              card_ids: selectedCardIds.map(String),
            },
          });
        }),
        ...deselectedPublications
          .filter((publication) => publication.config_values?.singlePayOption)
          .map((publication) => {
            const currentConfigValues = publication.config_values ?? {};
            const updatedConfigValues = { ...currentConfigValues };
            delete updatedConfigValues.singlePayOption;
            delete updatedConfigValues.card_ids;

            return changePublicationConfigValues({
              publicationId: publication.publication_id,
              configValues: updatedConfigValues,
            });
          }),
      ]);

      const removedCount = deselectedPublications.filter(
        (publication) => publication.config_values?.singlePayOption,
      ).length;
      const totalUpdated = selectedPublications.length + removedCount;

      enqueueSnackbar(`Se aplicaron los cambios a ${totalUpdated} publicaciones`, {
        variant: 'success',
      });

      await loadResults(selectedCardIds);
      onApplied?.();
    } catch {
      enqueueSnackbar('Error al aplicar los cambios', { variant: 'error' });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false}>
      <div className={classes.body}>
        <div className={classes.header}>
          <div className={classes.titleBlock}>
            <span className={classes.title}>1 Pago</span>
            <span className={classes.subtitle}>
              Seleccioná tarjetas para consultar publicaciones y formas de pago asociadas.
            </span>
          </div>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {error && <div className={classes.errorState}>{error}</div>}

        <div className={classes.panel}>
          <div className={classes.panelHeader}>
            <p className={classes.sectionTitle}>
              <CreditCardIcon style={{ fontSize: 16, color: FORM_COLORS.pink }} />
              Tarjetas 1 pago
            </p>
            <span className={classes.selectedCount}>
              {selectedCardIds.length}
              {' seleccionadas'}
            </span>
          </div>

          <div className={classes.selectWrap}>
            <ZoneMultiSelect
              options={cardOptions}
              selected={selectedCardIdsAsString}
              onChange={handleCardSelectionChange}
              placeholder="Seleccioná tarjetas de 1 pago..."
              loading={loadingCards}
            />
          </div>
          {!loadingCards && cardOptions.length === 0 && (
            <Typography className={classes.emptyState}>
              No hay tarjetas disponibles.
            </Typography>
          )}
        </div>

        <div className={classes.tablesGrid}>
          <PublicationsTable
            rows={result.publications}
            loading={loadingResults}
            selectedPublicationIds={selectedPublicationIds}
            onTogglePublication={handleTogglePublication}
            onToggleAllPublications={handleToggleAllPublications}
            emptyMessage={
              selectedCardIds.length
                ? 'No hay publicaciones para las tarjetas seleccionadas.'
                : 'Seleccioná una o más tarjetas para consultar publicaciones.'
            }
          />
          <DataTable
            title="Forma de pago asociadas"
            rows={result.paymentMethods}
            columns={PROMOTION_COLUMNS}
            loading={loadingResults}
            rowKey={(row) => String(row.promotion_id)}
            wrapClassName={classes.promotionsTableWrap}
            tableClassName={classes.promotionsTable}
            getCellClassName={(columnId) =>
              columnId === 'promotion_id' ? classes.promotionIdCell : classes.promotionNameCell
            }
            emptyMessage={
              selectedCardIds.length
                ? 'No hay formas de pago asociadas.'
                : 'Seleccioná una o más tarjetas para consultar formas de pago.'
            }
          />
        </div>

        <div className={classes.footer}>
          <button type="button" className={classes.ghostButton} onClick={onClose}>
            Cerrar
          </button>
          <button
            type="button"
            className={classes.primaryButton}
            disabled={applying || loadingResults || !selectedCardIds.length}
            onClick={handleApply}
          >
            {applying ? 'Aplicando...' : 'Aplicar'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
